import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { AllowedSupplierResponseModel } from './models/allowed-supplier-response-model';

@Injectable({
  providedIn: 'root',
})
export class AllowedSupplierAPIService extends BehaviorSubject<GridDataResult> {
    public baseUrlLocal: string = environment.apiBaseUrl;
    public loading: boolean = false;

    constructor(protected http: HttpClient) {
        super({ data: [], total: null });
    }

    public query(state: State, filter: CompositeFilterDescriptor): void {
        this.loading = true;
        this.fetch(state, filter).subscribe((x) => super.next(x));
    }

    protected fetch(state: State, filter: CompositeFilterDescriptor): Observable<GridDataResult> {
        let queryStart = '';
        let queryString = '';

        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pageindex=${state.skip}`);
        queryStart = this.getQueryStart(queryStart);
        queryString = queryString.concat(`${queryStart}pagesize=${state.take}`);

        const supplierIdFilter = this.getStateFilterValue(filter, 'supplierId');
        if (supplierIdFilter != undefined && supplierIdFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}supplierId=${encodeURIComponent(supplierIdFilter)}`);
        }

        const supplierNameFilter = this.getStateFilterValue(filter, 'supplierName');
        if (supplierNameFilter != undefined && supplierNameFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}supplierName=${encodeURIComponent(supplierNameFilter)}`);
        }

        const defaultCurrencyCodeFilter = this.getStateFilterValue(filter, 'defaultCurrencyCode');
        if (defaultCurrencyCodeFilter != undefined && defaultCurrencyCodeFilter.length >= 1) {
            queryString = queryString.concat(`${queryStart}defaultCurrencyCode=${encodeURIComponent(defaultCurrencyCodeFilter)}`);
        }

        const businessContactEmailFilter = this.getStateFilterValue(filter, 'businessContactEmail');
        if (businessContactEmailFilter != undefined && businessContactEmailFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}businessContactEmail=${encodeURIComponent(businessContactEmailFilter)}`);
        }

        const technicalContactEmailFilter = this.getStateFilterValue(filter, 'technicalContactEmail');
        if (technicalContactEmailFilter != undefined && technicalContactEmailFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}technicalContactEmail=${encodeURIComponent(technicalContactEmailFilter)}`);
        }

        const serviceNowGroupFilter = this.getStateFilterValue(filter, 'serviceNowGroup');
        if (serviceNowGroupFilter != undefined && serviceNowGroupFilter.length >= 3) {
            queryString = queryString.concat(`${queryStart}serviceNowGroup=${encodeURIComponent(serviceNowGroupFilter)}`);
        }

        const isActiveFilter = this.getStateFilterValue(filter, 'isActive');
        if (isActiveFilter != undefined) {
            queryString = queryString.concat(`${queryStart}isActive=${encodeURIComponent(isActiveFilter)}`);
        }

        return this.http.get<GridDataResult>(this.baseUrlLocal + `allowedsuppliers/search` + queryString)
          .pipe(
              map(
              (response: GridDataResult) => {
                  return {
                      data: response.data,
                      total: response.total
                  };
              }
              ), tap(() => (this.loading = false))
          );
    }

    private getQueryStart(queryString: string): string {
        return queryString === '' ? '?' : '&';
    }

    private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
        if (!filter?.filters?.length) {
            return undefined;
        }
        const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
            return !('filters' in item) && item.field === field;
        });
        if (matchingFilter?.value === undefined || matchingFilter.value === null) {
            return undefined;
        }
        return String(matchingFilter.value);
    }
}

@Injectable()
export class AllowedSupplierService extends AllowedSupplierAPIService {
    constructor(http: HttpClient) {
        super(http);
    }

    public create(model: AllowedSupplierResponseModel): Observable<AllowedSupplierResponseModel> {
        return this.http.post<AllowedSupplierResponseModel>(this.baseUrlLocal + `allowedsuppliers`, model);
    }

    public update(supplierId: string, model: AllowedSupplierResponseModel): Observable<AllowedSupplierResponseModel> {
        return this.http.put<AllowedSupplierResponseModel>(this.baseUrlLocal + `allowedsuppliers/${encodeURIComponent(supplierId)}`, model);
    }

    public delete(supplierId: string): Observable<void> {
        return this.http.delete<void>(this.baseUrlLocal + `allowedsuppliers/${encodeURIComponent(supplierId)}`);
    }
}
